<template>
  <v-dialog v-model="showL4tDeviceMatrixDialog" max-width="1100" @click:outside="closeDialog">
    <v-card>
      <v-card-title>
        <v-row align="center">
          <v-col cols="8">
            <span class="text-h6">Jetson L4T Device Matrix</span>
            <v-chip small class="ml-2" color="grey lighten-2">{{ entries.length }} entries</v-chip>
          </v-col>
          <v-col cols="4" class="text-right">
            <v-btn icon small color="primary" @click="fetchEntries" :loading="loading" class="mr-1">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn icon @click="closeDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text>
        <!-- Add / Edit Form -->
        <v-expand-transition>
          <v-card v-if="showForm" outlined class="mb-3 pa-3">
            <v-row dense>
              <v-col cols="3">
                <v-text-field
                  v-model="formEntry.l4t_version"
                  label="L4T Version"
                  placeholder="e.g. 36.4"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="4">
                <v-combobox
                  v-model="formEntry.supported_devices"
                  label="Supported Devices"
                  placeholder="Type and press Enter"
                  dense
                  outlined
                  hide-details
                  multiple
                  small-chips
                  deletable-chips
                ></v-combobox>
              </v-col>
              <v-col cols="3">
                <v-combobox
                  v-model="formEntry.cudas"
                  label="CUDA Versions"
                  placeholder="e.g. 12.6"
                  dense
                  outlined
                  hide-details
                  multiple
                  small-chips
                  deletable-chips
                ></v-combobox>
              </v-col>
              <v-col cols="2" class="d-flex align-center">
                <v-btn small color="primary" @click="saveForm" :loading="saving" class="mr-1">
                  <v-icon small left>mdi-check</v-icon>{{ formMode === 'edit' ? 'Update' : 'Save' }}
                </v-btn>
                <v-btn small outlined @click="cancelForm">Cancel</v-btn>
              </v-col>
            </v-row>
          </v-card>
        </v-expand-transition>

        <!-- JSON Import -->
        <v-expand-transition>
          <v-card v-if="showJsonImport" outlined class="mb-3 pa-3">
            <v-textarea
              v-model="jsonImportText"
              label="Paste JSON (array format)"
              :placeholder="jsonPlaceholder"
              dense
              outlined
              rows="5"
              hide-details
              class="mb-2"
              style="font-family: monospace; font-size: 12px;"
            ></v-textarea>
            <div class="d-flex">
              <v-btn small color="primary" @click="importJson" :loading="saving" class="mr-1">
                <v-icon small left>mdi-import</v-icon>Import
              </v-btn>
              <v-btn small outlined @click="showJsonImport = false; jsonImportText = ''">Cancel</v-btn>
            </div>
          </v-card>
        </v-expand-transition>

        <!-- Action Buttons Row -->
        <v-row dense class="mb-1">
          <v-col cols="12" class="d-flex align-center">
            <v-btn small color="primary" outlined @click="openAddForm" v-if="!showForm">
              <v-icon small left>mdi-plus</v-icon>Add Entry
            </v-btn>
            <v-btn small outlined class="ml-2" @click="showJsonImport = !showJsonImport" v-if="!showJsonImport">
              <v-icon small left>mdi-code-json</v-icon>JSON Import
            </v-btn>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              clearable
              dense
              hide-details
              label="Search"
              outlined
              prepend-inner-icon="mdi-magnify"
              style="max-width: 220px;"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Data Table -->
        <v-data-table
          :headers="headers"
          :items="entries"
          :search="search"
          dense
          class="elevation-1"
          no-data-text="No L4T device matrix entries"
          :items-per-page="15"
          sort-by="l4t_version"
          sort-desc
        >
          <template v-slot:item.l4t_version="{ item }">
            <v-chip small label color="deep-purple" dark>L4T {{ item.l4t_version }}</v-chip>
          </template>

          <template v-slot:item.supported_devices="{ item }">
            <v-chip
              v-for="(device, idx) in item.supported_devices"
              :key="idx"
              x-small
              class="mr-1 mb-1"
              color="teal lighten-4"
            >{{ device }}</v-chip>
          </template>

          <template v-slot:item.cudas="{ item }">
            <v-chip
              v-for="(cuda, idx) in item.cudas"
              :key="idx"
              x-small
              class="mr-1 mb-1"
              color="blue lighten-4"
            >{{ cuda }}</v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="startEdit(item)">
              <v-icon small color="primary">mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small @click="deleteEntry(item)">
              <v-icon small color="error">mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import Swal from 'sweetalert2'
import { apiService } from '../../service/api.service'

export default {
  name: 'L4tDeviceMatrixDialog',
  data () {
    return {
      entries: [],
      search: '',
      loading: false,
      saving: false,
      showForm: false,
      showJsonImport: false,
      jsonImportText: '',
      formMode: 'add', // 'add' or 'edit'
      formEntry: {
        id: null,
        l4t_version: '',
        supported_devices: [],
        cudas: []
      },
      headers: [
        { text: 'L4T Version', value: 'l4t_version', align: 'start', sortable: true, width: '130px' },
        { text: 'Supported Devices', value: 'supported_devices', align: 'start', sortable: false },
        { text: 'CUDA Versions', value: 'cudas', align: 'start', sortable: false },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false, width: '100px' }
      ],
      jsonPlaceholder: '[{"l4t_version": "36.4", "supported_devices": ["AGX Orin", "Orin NX"], "cudas": ["12.6", "12.8"]}]'
    }
  },
  props: {
    showL4tDeviceMatrixDialog: Boolean
  },
  watch: {
    showL4tDeviceMatrixDialog (val) {
      if (val) {
        this.fetchEntries()
      }
    }
  },
  methods: {
    closeDialog () {
      this.showForm = false
      this.showJsonImport = false
      this.$emit('close-l4t-device-matrix')
    },
    async fetchEntries () {
      this.loading = true
      try {
        const response = await apiService.get('admin/l4t-device-matrix')
        if (response.data && response.data.data) {
          this.entries = response.data.data
        }
      } catch (error) {
        console.error('Failed to fetch L4T device matrix:', error)
        Swal.fire('Error', 'Failed to fetch L4T device matrix.', 'error')
      } finally {
        this.loading = false
      }
    },
    openAddForm () {
      this.formMode = 'add'
      this.formEntry = { id: null, l4t_version: '', supported_devices: [], cudas: [] }
      this.showForm = true
    },
    startEdit (item) {
      this.formMode = 'edit'
      this.formEntry = {
        id: item.id,
        l4t_version: item.l4t_version,
        supported_devices: [...(item.supported_devices || [])],
        cudas: [...(item.cudas || [])]
      }
      this.showForm = true
    },
    cancelForm () {
      this.showForm = false
      this.formEntry = { id: null, l4t_version: '', supported_devices: [], cudas: [] }
    },
    async saveForm () {
      if (!this.formEntry.l4t_version) {
        Swal.fire('Warning', 'L4T Version is required.', 'warning')
        return
      }

      const payload = {
        l4t_version: this.formEntry.l4t_version,
        supported_devices: this.formEntry.supported_devices,
        cudas: this.formEntry.cudas
      }

      this.saving = true
      try {
        if (this.formMode === 'edit' && this.formEntry.id) {
          await apiService.put(`admin/l4t-device-matrix/${this.formEntry.id}`, payload)
          Swal.fire('Success', 'Entry updated.', 'success')
        } else {
          await apiService.post('admin/l4t-device-matrix', payload)
          Swal.fire('Success', 'Entry created.', 'success')
        }
        this.cancelForm()
        await this.fetchEntries()
      } catch (error) {
        const msg = error.response?.data?.message || 'Operation failed.'
        Swal.fire('Error', msg, 'error')
      } finally {
        this.saving = false
      }
    },
    async deleteEntry (item) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `Delete L4T ${item.l4t_version}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!'
      })
      if (result.isConfirmed) {
        try {
          await apiService.delete(`admin/l4t-device-matrix/${item.id}`)
          Swal.fire('Deleted!', 'Entry has been deleted.', 'success')
          await this.fetchEntries()
        } catch (error) {
          Swal.fire('Error', 'Failed to delete entry.', 'error')
        }
      }
    },
    async importJson () {
      if (!this.jsonImportText.trim()) {
        Swal.fire('Warning', 'Please paste JSON content.', 'warning')
        return
      }
      let parsed
      try {
        parsed = JSON.parse(this.jsonImportText.trim())
      } catch (e) {
        Swal.fire('Error', 'Invalid JSON format.', 'error')
        return
      }

      if (!Array.isArray(parsed)) {
        Swal.fire('Error', 'Expected a JSON array of L4T entries.', 'error')
        return
      }

      // Validate structure
      for (const entry of parsed) {
        if (!entry.l4t_version) {
          Swal.fire('Error', 'Each entry must have l4t_version.', 'error')
          return
        }
      }

      this.saving = true
      let successCount = 0
      let failCount = 0

      for (const entry of parsed) {
        const payload = {
          l4t_version: String(entry.l4t_version),
          supported_devices: entry.supported_devices || [],
          cudas: entry.cudas || []
        }
        try {
          await apiService.post('admin/l4t-device-matrix', payload)
          successCount++
        } catch (error) {
          // If already exists, try update
          const existing = this.entries.find(e => e.l4t_version === payload.l4t_version)
          if (existing) {
            try {
              await apiService.put(`admin/l4t-device-matrix/${existing.id}`, payload)
              successCount++
            } catch (updateError) {
              failCount++
            }
          } else {
            failCount++
          }
        }
      }

      this.saving = false
      this.showJsonImport = false
      this.jsonImportText = ''
      await this.fetchEntries()
      Swal.fire('Import Complete', `${successCount} succeeded, ${failCount} failed.`, successCount > 0 ? 'success' : 'error')
    }
  },
  mounted () {
    if (this.showL4tDeviceMatrixDialog) {
      this.fetchEntries()
    }
  }
}
</script>
